(() => {
  if (customElements.get('varia-grind-distribution')) return;
  const cache = new Map();
  const colors = ['#171717', '#b07832', '#39748f', '#a04761', '#577451', '#7662a3'];
  let instances = 0;
  customElements.define('varia-grind-distribution', class extends HTMLElement {
    async connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.instanceId ||= 'vgd-' + (++instances);
      this.q = selector => this.querySelector(selector);
      this.labels = JSON.parse(this.q('[data-labels]').textContent);
      this.controller = new AbortController();
      const connection = this.controller;
      const options = { signal: this.controller.signal };
      this.addEventListener('change', event => {
        const target = event.target;
        if (target.matches('[data-geometry]')) this.geometry = target.value;
        if (target.matches('[data-burr-select]')) this.burr = target.value;
        if (target.matches('[data-setting]')) this.setting = Number(target.value);
        if (target.matches('[data-speed]')) this.speed = target.value === 'null' ? null : Number(target.value);
        if (target.matches('[data-view]')) this.view = target.value;
        if (target.matches('[data-comparison]')) this.comparison = target.value;
        this.render();
      }, options);
      this.q('[data-bin]').addEventListener('input', event => this.inspect(Number(event.target.value)), options);
      this.q('svg').addEventListener('pointermove', event => {
        const rect = event.currentTarget.getBoundingClientRect();
        if (!rect.width || !this.report) return;
        const x = (event.clientX - rect.left) / rect.width * this.axis.width;
        const fraction = Math.max(0, Math.min(1, (x - this.axis.left) / (this.axis.right - this.axis.left)));
        const size = 10 ** (-2 + fraction * 6);
        const index = this.report.bins.findIndex(bin => bin[1] >= size);
        this.inspect(index < 0 ? this.report.bins.length - 1 : index);
      }, options);
      this.q('[data-retry]').addEventListener('click', () => {
        this.disconnectedCallback();
        this.connectedCallback();
      }, options);
      try {
        if (!this.reports) {
          if (!cache.has(this.dataset.source)) {
            cache.set(this.dataset.source, fetch(this.dataset.source)
              .then(response => { if (!response.ok) throw Error('report'); return response.json(); })
              .catch(error => { cache.delete(this.dataset.source); throw error; }));
          }
          this.reports = await cache.get(this.dataset.source);
          if (!Array.isArray(this.reports) || !this.reports.length || this.reports.some(report =>
            !report.burr || !report.name || !Array.isArray(report.bins) || !report.bins.length ||
            ![report.setting, report.d10, report.d50, report.d90].every(Number.isFinite) ||
            report.bins.some(bin => bin.length !== 4 || !bin.every(Number.isFinite)))) throw Error('data');
          this.geometry = this.dataset.geometry || 'flat';
          this.burr = this.dataset.burr || 'ss';
          this.setting = 3;
          this.speed = null;
          this.bin = 50;
          this.view = 'cumulative';
          this.comparison = 'none';
        }
        if (!this.connected || connection !== this.controller) return;
        this.q('[data-interface]').hidden = false;
        this.q('[data-status]').hidden = true;
        this.q('[data-retry]').hidden = true;
        this.render();
        if (window.ResizeObserver) {
          this.observer = new ResizeObserver(() => { if (this.series) this.draw(); });
          this.observer.observe(this.q('svg'));
        } else window.addEventListener('resize', () => { if (this.series) this.draw(); }, options);
      } catch (error) {
        if (!this.connected || connection !== this.controller) return;
        this.reports = null;
        cache.delete(this.dataset.source);
        this.q('[data-interface]').hidden = true;
        this.q('[data-status]').hidden = false;
        this.q('[data-status]').textContent = this.labels.load_error;
        this.q('[data-retry]').hidden = false;
      }
    }
    disconnectedCallback() {
      this.connected = false;
      this.controller?.abort();
      this.observer?.disconnect();
    }
    fmt(value) {
      return Number(value).toLocaleString(document.documentElement.lang || 'en', { maximumFractionDigits: 3 });
    }
    select(selector, items, value) {
      const select = this.q(selector);
      select.replaceChildren(...items.map(([key, label]) => new Option(label, String(key))));
      select.value = String(value);
    }
    formatName(geometry) {
      return this.labels[geometry === 'flat' ? 'flat_format' : 'conical_format'];
    }
    reportName(report) {
      return report.name + ' · #' + report.setting + (report.rpm === null ? '' : ' · ' + report.rpm + ' RPM');
    }
    render() {
      const q = this.q;
      const geometries = [...new Set(this.reports.map(report => report.geometry || 'conical'))];
      if (!geometries.includes(this.geometry)) this.geometry = geometries[0];
      this.select('[data-geometry]', geometries.map(geometry => [geometry, this.formatName(geometry)]), this.geometry);
      q('[data-geometry-label]').hidden = geometries.length < 2 || this.dataset.geometryLock === 'true';
      q('[data-format-caption]').textContent = this.dataset.model === 'vs6' ? ' · ' + this.formatName(this.geometry) : '';
      const family = this.reports.filter(report => (report.geometry || 'conical') === this.geometry);
      const burrs = [...new Map(family.map(report => [report.burr, report.name]))];
      if (!burrs.some(([burr]) => burr === this.burr)) this.burr = burrs[0][0];
      this.select('[data-burr-select]', burrs, this.burr);
      const available = family.filter(report => report.burr === this.burr);
      const settings = [...new Set(available.map(report => report.setting))].sort((a, b) => a - b);
      if (!settings.includes(this.setting)) this.setting = settings[0];
      this.select('[data-setting]', settings.map(setting => [setting, '#' + setting]), this.setting);
      const selected = available.filter(report => report.setting === this.setting);
      const speeds = [...new Set(selected.map(report => report.rpm))].sort((a, b) => (a || 0) - (b || 0));
      if (!speeds.includes(this.speed)) this.speed = speeds[0];
      this.select('[data-speed]', speeds.map(speed => [speed, speed === null ? this.labels.speed_unspecified : speed + ' RPM']), this.speed);
      q('[data-speed-label]').hidden = speeds.length === 1 && speeds[0] === null;
      this.report = selected.find(report => report.rpm === this.speed);
      const matchingBurrs = family.filter(report => report.setting === this.setting && report.rpm === this.speed);
      const modes = [['none', this.labels.single_report]];
      if (selected.length > 1) modes.push(['speeds', this.labels.compare_speeds]);
      if (matchingBurrs.length > 1) modes.push(['burrs', this.labels.compare_burrs]);
      if (!modes.some(([mode]) => mode === this.comparison)) this.comparison = 'none';
      this.select('[data-comparison]', modes, this.comparison);
      let series = this.comparison === 'speeds' ? selected : this.comparison === 'burrs' ? matchingBurrs : [this.report];
      this.series = [this.report, ...series.filter(report => report !== this.report)];
      const sameIntervals = this.series.every(report => report.bins.length === this.report.bins.length &&
        report.bins.every((bin, index) => bin[0] === this.report.bins[index][0] && bin[1] === this.report.bins[index][1]));
      q('[data-view] option[value="interval"]').disabled = !sameIntervals;
      if (!sameIntervals && this.view === 'interval') this.view = 'cumulative';
      q('[data-view]').value = this.view;
      q('[data-comparison-note]').hidden = sameIntervals;
      q('[data-focused-report]').textContent = this.labels.selected_report + ': ' + this.reportName(this.report);
      this.draw();
      for (const key of ['d10', 'd50', 'd90']) q('[data-' + key + ']').textContent = this.fmt(this.report[key]) + ' µm';
      q('[data-bin]').max = this.report.bins.length - 1;
      this.inspect(Math.min(this.bin, this.report.bins.length - 1));
      q('[data-method]').textContent = this.report.instrument + ' · ' + this.labels.method_context + ' ' +
        (this.report.derived ? this.labels.derived_intervals : this.labels.source_intervals);
      q('[data-report]').textContent = this.report.file + ' · ' + this.labels.measured + ' ' + this.report.date;
      q('[data-report-link]').hidden = !this.report.url;
      q('[data-source-unavailable]').hidden = Boolean(this.report.url);
      if (this.report.url) q('[data-report-link]').href = this.report.url;
      const rows = this.report.bins.map(bin => {
        const row = document.createElement('tr');
        for (const text of [this.fmt(bin[0]) + '–' + this.fmt(bin[1]), bin[2].toFixed(2), bin[3].toFixed(2)]) {
          const cell = document.createElement('td'); cell.textContent = text; row.append(cell);
        }
        return row;
      });
      q('[data-values]').replaceChildren(...rows);
    }
    draw() {
      const svg = this.q('svg');
      const width = Math.max(300, Math.round(svg.getBoundingClientRect().width || this.clientWidth || 900));
      const left = 50, right = width - 26, bottom = 290, top = 45;
      this.axis = { left, right, width };
      const ymax = this.view === 'cumulative' ? 100 : Math.max(2, Math.ceil(Math.max(...this.series.flatMap(report => report.bins.map(bin => bin[2]))) / 2) * 2);
      const x = size => left + (Math.log10(size) + 2) / 6 * (right - left);
      const y = volume => bottom - volume / ymax * (bottom - top);
      const make = (tag, attrs, text) => {
        const node = document.createElementNS('http://www.w3.org/2000/svg', tag);
        for (const [key, value] of Object.entries(attrs)) node.setAttribute(key, value);
        if (text !== undefined) node.textContent = text;
        return node;
      };
      const titleId = this.instanceId + '-title';
      const nodes = [make('title', { id: titleId }, this.reportName(this.report) + ' · ' + this.labels.measured_distribution)];
      svg.setAttribute('viewBox', '0 0 ' + width + ' 365');
      svg.setAttribute('aria-labelledby', titleId);
      for (let i = 0; i <= 5; i++) {
        const value = ymax * i / 5;
        nodes.push(make('line', { x1: left, x2: right, y1: y(value), y2: y(value), stroke: '#e5e5e5' }));
        nodes.push(make('text', { x: left - 10, y: y(value) + 4, 'text-anchor': 'end' }, this.fmt(value)));
      }
      const ticks = width < 500 ? [.01, 1, 100, 10000] : [.01, .1, 1, 10, 100, 1000, 10000];
      for (const tick of ticks) nodes.push(make('text', { x: x(tick), y: 318, 'text-anchor': 'middle' }, this.fmt(tick)));
      nodes.push(make('text', { x: (left + right) / 2, y: 349, 'text-anchor': 'middle' }, this.labels.particle_axis));
      nodes.push(make('text', { x: left, y: 24 }, this.labels[this.view === 'cumulative' ? 'cumulative_axis' : 'interval_axis']));
      this.series.forEach((report, index) => {
        const points = report.bins.map(bin => [this.view === 'cumulative' ? bin[1] : Math.sqrt(bin[0] * bin[1]), bin[this.view === 'cumulative' ? 3 : 2]]);
        const path = points.map((point, i) => (i ? 'L' : 'M') + x(point[0]).toFixed(2) + ',' + y(point[1]).toFixed(2)).join(' ');
        nodes.push(make('path', { class: 'vgd-curve', stroke: colors[index % colors.length], 'stroke-width': index ? 2 : 3, d: path }));
      });
      svg.replaceChildren(...nodes);
      this.q('[data-legend]').replaceChildren(...this.series.map((report, index) => {
        const item = document.createElement('span');
        item.style.borderColor = colors[index % colors.length];
        item.textContent = this.reportName(report);
        if (!index) item.className = 'vgd-legend__selected';
        return item;
      }));
    }
    inspect(index) {
      this.bin = Math.max(0, Math.min(this.report.bins.length - 1, index));
      this.q('[data-bin]').value = this.bin;
      const bin = this.report.bins[this.bin];
      this.q('[data-readout]').textContent = this.fmt(bin[0]) + '–' + this.fmt(bin[1]) + ' µm: ' +
        bin[2].toFixed(2) + '% ' + this.labels.in_interval + ' · ' + bin[3].toFixed(2) + '% ' +
        this.labels.below + ' ' + this.fmt(bin[1]) + ' µm.';
    }
  });
})();
